import type { HeroData } from './components/Hero'
import type { Topic } from './api-types'
import { useTopic, HERO_TOPIC_ENDPOINT } from './topic'

// The endpoint moved next to the shared `useTopic` loader (topic.ts), which the
// hero and the browse highlight now both go through. Re-exported here so the
// hero's own module stays the one place a caller has to look.
export { HERO_TOPIC_ENDPOINT }

/**
 * Map a {@link Topic} to the {@link HeroData} the banner renders.
 *
 * `teaser` is the plain-text copy (`description` is an HTML fragment, which the
 * banner does not render), and a topic can carry several `related_movies` — the
 * first one is the title the banner features.
 */
export const createHeroData = (topic: Topic): HeroData => {
  const movie = topic.related_movies?.[0]
  return {
    backgroundUrl: topic.image_path?.href ?? '',
    logoUrl: topic.visual_title?.href,
    // The lockup's intrinsic size, so the banner can reserve its box before the
    // image loads rather than reflowing the copy under it — see HeroData.
    logoWidth: topic.visual_title?.width,
    logoHeight: topic.visual_title?.height,
    title: topic.title,
    subtitle: topic.subtitle,
    text: topic.teaser ?? '',
    movieId: movie?.id,
    media_type: movie?.media_type,
    // What the banner's "read more" action points at — the topic itself.
    topicId: topic.id,
  }
}

export interface HeroTopicState {
  /** The banner content, or `null` when the domain has no hero topic. */
  hero: HeroData | null;
  loading: boolean;
}

/**
 * Load the feature banner content from the content API.
 *
 * **A domain without a configured hero answers `404`** — that is the documented
 * "no content" answer, not an error, so it resolves to `hero: null` and the caller
 * renders no banner at all. Any other failure logs and degrades the same way; see
 * {@link useTopic}, which owns that request/degradation contract.
 */
export const useHeroTopic = (): HeroTopicState => {
  const { topic, loading } = useTopic(HERO_TOPIC_ENDPOINT)
  return { hero: topic ? createHeroData(topic) : null, loading }
}
